if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.toggleState = false;
      this.activeTrigger = null;
      this.initialized = false;
      this.triggerObserver = null;
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      if (!this.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
        this.querySelector('.quick-cart-drawer__blocks').inert = true;
      }

      if (!this.initialized) {
        this.initialized = true;
        this.querySelector('.button--close').addEventListener('click', this.close.bind(this));
        this.querySelector('.quick-cart-drawer__backdrop').addEventListener('click', this.close.bind(this));

        this.querySelector('.quick-cart-drawer__blocks').addEventListener('keydown', e => {
          if (e.key === 'Escape' && this.toggleState) {
            e.preventDefault();
            this.close();
            return;
          }

          if (this.toggleState) {
            const focusableElements = [...this.querySelectorAll(
              'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )].filter(element => element.getClientRects().length > 0);
            const firstFocusableElement = focusableElements[0];
            const lastFocusableElement = focusableElements[focusableElements.length - 1];

            if (e.key !== 'Tab' || !firstFocusableElement || !lastFocusableElement) return;

            if (e.shiftKey) {
              if (document.activeElement === firstFocusableElement) {
                e.preventDefault();
                lastFocusableElement.focus();
              }
            } else if (document.activeElement === lastFocusableElement) {
              e.preventDefault();
              firstFocusableElement.focus();
            }
          }
        });
      }

      this.initTriggers();
    }

    initTriggers() {
      const bindTriggers = () => {
        document.querySelectorAll('.quick-cart-drawer__trigger').forEach(element => {
          if (!element._quickCartDrawerInit) {
            element.addEventListener('click', event => {
              this.fetchProductForQuickCartDrawer(event);
            });
            element._quickCartDrawerInit = true;
          }
        });
      };

      bindTriggers();
      if (this.triggerObserver) return;

      this.triggerObserver = new MutationObserver(bindTriggers);
      this.triggerObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    disconnectedCallback() {
      this.triggerObserver?.disconnect();
    }

    updateDrawerTitle(trigger) {
      const title = this.querySelector('#QuickCartDrawerTitle');
      const label = trigger?.dataset.quickCartTitle?.trim();
      if (title && label) title.textContent = label;
    }

    findSizeFieldset(root, trigger) {
      if (trigger?.dataset.quickCartPurpose !== 'size') return null;

      const fieldsets = [...root.querySelectorAll('fieldset')];
      const sizeNamePattern = /m[aă]rim|\bsize\b|pedia\s+rainwear/i;
      const labelledFieldset = fieldsets.find(fieldset => {
        const legend = fieldset.querySelector(':scope > legend, legend');
        return sizeNamePattern.test(legend?.textContent || '');
      });

      if (labelledFieldset) return labelledFieldset;
      return fieldsets.length === 1 ? fieldsets[0] : null;
    }

    normalizeSizeLegend(fieldset) {
      const legend = fieldset?.querySelector(':scope > legend, legend');
      if (!legend) return;

      const selectedValue = legend.querySelector('[data-selected-variant], [data-selected-value]');
      const textNode = [...legend.childNodes].find(node =>
        node.nodeType === Node.TEXT_NODE && node.textContent.trim()
      );

      if (textNode) textNode.textContent = 'Mărime ';
      else legend.insertBefore(document.createTextNode('Mărime '), selectedValue || legend.firstChild);
    }

    groupSizeOptions(fieldset) {
      if (!fieldset || fieldset.querySelector(':scope > .product-form__size-options')) return;

      const optionButtons = [...fieldset.querySelectorAll(':scope > .button--variant')];
      if (!optionButtons.length) return;

      const optionsGroup = document.createElement('div');
      optionsGroup.className = 'product-form__size-options';
      optionButtons[0].insertAdjacentElement('beforebegin', optionsGroup);
      optionButtons.forEach(optionButton => optionsGroup.append(optionButton));
    }

    remapGuideIds(guide) {
      const suffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const idMap = new Map();

      guide.querySelectorAll('[id]').forEach(element => {
        const oldId = element.id;
        const newId = `${oldId}-quick-cart-${suffix}`;
        idMap.set(oldId, newId);
        element.id = newId;
      });

      guide.querySelectorAll('[aria-controls], [aria-labelledby]').forEach(element => {
        ['aria-controls', 'aria-labelledby'].forEach(attribute => {
          const oldValue = element.getAttribute(attribute);
          if (!oldValue) return;
          const newValue = oldValue
            .split(/\s+/)
            .map(value => idMap.get(value) || value)
            .join(' ');
          element.setAttribute(attribute, newValue);
        });
      });
    }

    async attachSizeGuide(trigger, fieldset) {
      if (!fieldset || fieldset.querySelector('.carousel-size-guide')) return;

      try {
        const productUrl = new URL(trigger.dataset.productUrl, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);
        ['preview_theme_id', 'pb'].forEach(parameter => {
          if (currentParams.has(parameter)) productUrl.searchParams.set(parameter, currentParams.get(parameter));
        });

        const response = await fetch(productUrl.toString());
        if (!response.ok) return;

        const html = await response.text();
        const source = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.product-form__input--size .carousel-size-guide, .carousel-size-guide');
        if (!source || fieldset.querySelector('.carousel-size-guide')) return;

        const guide = source.cloneNode(true);
        this.remapGuideIds(guide);
        const legend = fieldset.querySelector(':scope > legend, legend');
        legend?.insertAdjacentElement('afterend', guide);
      } catch (_) {
        // The quick add remains fully usable when a guide is unavailable.
      }
    }

    enhanceSizePresentation(trigger) {
      const quickProduct = this.querySelector('.quick-cart-product');
      const fieldset = this.findSizeFieldset(quickProduct, trigger);
      if (!fieldset) return;

      fieldset.classList.add('product-form__input--size');
      this.normalizeSizeLegend(fieldset);
      this.groupSizeOptions(fieldset);
      this.attachSizeGuide(trigger, fieldset);
    }

    async fetchProductForQuickCartDrawer(event) {
      const trigger = event.target.closest('.quick-cart-drawer__trigger[data-product-url], [data-product-url].quick-cart-drawer__trigger');
      if (!trigger) return;
      this.activeTrigger = trigger;
      this.updateDrawerTitle(trigger);
      this.toggleAriaExpanded(trigger);

      /** stop autoplay on quick cart trigger */
      if (trigger.closest('card-product-slider')) {
        trigger.closest('card-product-slider').slider.autoplay.stop();
        trigger.closest('card-product-slider').classList.add('product--open-on-quick-cart');
      }

      const swiperReady = typeof window.loadCarouselSwiper === 'function'
        ? window.loadCarouselSwiper()
        : Promise.resolve(window.Swiper);

      const isRecommendations = trigger.classList.contains('quick-cart-drawer__trigger--recommendations');

      const productCard = trigger.closest('product-card');
      let productOptions = null;

      if (!isRecommendations) {
        if (!productCard) return;
        // get all checked options
        productOptions = productCard.querySelectorAll('input[type="radio"]:checked, select');
      }

      let quickProductLoaded = false;
      trigger.classList.add('is--loading');
      try {
        // if root contains locale, add locale to productUrl as prefix
        let fetchPrefix = '';
        if (window.Shopify.routes.root.includes(`/${window.Shopify.locale}/`)) {
          fetchPrefix = `/${window.Shopify.locale}`;
        }

        const productCardResponse = await fetch(`${fetchPrefix}${trigger.dataset.productUrl}/?view=quick-cart`);
        if (!productCardResponse) return;

        const productCardHTML = await productCardResponse.text();
        const productCard = document.createElement('DIV');
        productCard.insertAdjacentHTML('beforeend', productCardHTML);
        if (!productCard.querySelector('.quick-cart-product')) return;
        quickProductLoaded = true;
        this.querySelector('.quick-cart-drawer__main').innerHTML = '';
        this.querySelector('.quick-cart-drawer__main').append(productCard.querySelector('.quick-cart-product'));
        this.enhanceSizePresentation(trigger);

        await swiperReady;
        this.sliderInstance = new Swiper('.quick-cart-drawer__media-swiper', {
          slidesPerView: 1.25,
          spaceBetween: 8,
          freeMode: {
            enabled: true
          },
          breakpoints: {
            750: {
              slidesPerView: 2
            }
          }
        });
      } catch (error) {
        // console.log(error);
      } finally {
        trigger.classList.remove('is--loading');
        if (!quickProductLoaded) {
          this.toggleAriaExpanded();
          return;
        }

        if (!isRecommendations) {
          // update quick cart drawer options with product options
          productOptions.forEach(productOption => {
            let productOptionName = productOption.name;
            const optionSectionIdAttribute = productOption.dataset?.sectionId;

            if (optionSectionIdAttribute) {
              productOptionName = productOptionName.replace(optionSectionIdAttribute, '');
            }

            const quickCartDrawerOption = this.querySelector(
              `[name="${CSS.escape(productOptionName)}-quick-cart-product"][value="${CSS.escape(productOption.value)}"]`
            );
            if (!quickCartDrawerOption) return;
            const quickCartDrawerOptionLabel = quickCartDrawerOption.closest('fieldset')?.querySelector('legend');

            // before updating radio button state, remove checked attribute for specific name
            this.querySelectorAll(`[name="${CSS.escape(productOptionName)}-quick-cart-product"]`).forEach(radioInput => {
              radioInput.removeAttribute('checked');
              radioInput.closest('li')?.classList.remove('checked');
            });

            if (quickCartDrawerOption) {
              quickCartDrawerOption.setAttribute('checked', '');
              const quickCartDrawerOptionLabelSpan = quickCartDrawerOptionLabel?.querySelector('[data-selected-variant]');
              if (quickCartDrawerOptionLabelSpan) {
                quickCartDrawerOptionLabelSpan.innerHTML = productOption.value;
              }
            }

            // update variant id in product card with selected variant id
            const drawerVariantInput = this.querySelector('product-card input[name="id"]');
            const sourceVariantInput = productCard.querySelector('input[name="id"]');
            if (drawerVariantInput && sourceVariantInput) drawerVariantInput.value = sourceVariantInput.value;

            // update radio button state
            if (quickCartDrawerOption) {
              quickCartDrawerOption.closest('li').classList.add('checked');
            }

            // initilise for disabled states to be checked and updated
            this.querySelector('product-card')?.init();
          });
        }

        // short wait before opening drawer
        setTimeout(() => {
          this.open();
        }, 500);

        // add small delay to open drawer
        // this.open();

        // update label when variant is selected
        this.querySelector('.quick-cart-drawer__main')
          .querySelectorAll('.variant-option-radio-input')
          .forEach(radioInput => {
            radioInput.addEventListener('change', event => {
              const quickCartDrawerOptionLabel = event.target.parentElement.parentElement.querySelector('legend');
              const quickCartDrawerOptionLabelSpan = quickCartDrawerOptionLabel.querySelector('[data-selected-variant]');
              if (quickCartDrawerOptionLabelSpan) {
                quickCartDrawerOptionLabelSpan.innerHTML = event.target.value;
              }

              // update variant id in product card with selected variant id
              const selectedVariantId = this.querySelector(".product-card__add-to-cart--form input[name='id']").value;

              // get product variants data
              const productVariantsData = this.querySelector('product-card').variantsObj;
              const featuredMedia = productVariantsData.find(variant => variant.id == selectedVariantId)?.featured_media;

              if (featuredMedia) {
                this.setActiveMedia(featuredMedia.id);
              }
            });
          });
      }
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(media => Number(media.dataset.mediaId) === id);
      if (mediaFound) {
        this.sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    /** Drawer coreß functions */
    toggle() {
      if (!this.toggleState) {
        this.open();
      } else {
        this.close();
      }
    }

    open() {
      this.toggleState = true;
      document.querySelector('body').classList.add('overflow-hidden');
      const quickCartDrawer = this.querySelector('.quick-cart-drawer__blocks');
      const closeButton = quickCartDrawer.querySelector('.button--close');
      quickCartDrawer.inert = false;
      quickCartDrawer.setAttribute('tabindex', '0');
      closeButton.setAttribute('tabindex', '0');
      this.classList.add('is--open');
      this.opened();
      closeButton.focus();
    }

    close() {
      this.toggleState = false;

      const shopTheLookDrawer = document.querySelector('shop-the-look-drawer');

      if (shopTheLookDrawer && !shopTheLookDrawer.classList.contains('is--open')) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      if (!shopTheLookDrawer) {
        document.querySelector('body').classList.remove('overflow-hidden');
      }
      this.classList.remove('is--open');
      const quickCartDrawer = this.querySelector('.quick-cart-drawer__blocks');
      quickCartDrawer.inert = true;
      quickCartDrawer.setAttribute('tabindex', '-1');
      this.querySelector('.button--close').setAttribute('tabindex', '-1');
      this.closed();
      this.toggleAriaExpanded();

      const trigger = this.activeTrigger;
      this.activeTrigger = null;
      if (trigger?.isConnected) requestAnimationFrame(() => trigger.focus({ preventScroll: true }));

      /** start autoplay on quick cart drawe close */
      if (document.querySelector('card-product-slider.product--open-on-quick-cart')) {
        document.querySelector('card-product-slider.product--open-on-quick-cart').slider.autoplay.start();
        document.querySelector('card-product-slider.product--open-on-quick-cart').classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(trigger) {
      if (trigger) {
        trigger.setAttribute('aria-expanded', true);
        this.querySelector('.button--close').setAttribute('aria-expanded', true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(button => {
          button.setAttribute('aria-expanded', false);
        });
      }
    }

    opened() {
      const openedEvent = new Event('opened', { bubbles: true });
      this.dispatchEvent(openedEvent);
    }

    closed() {
      const closedEvent = new Event('closed', { bubbles: true });
      this.dispatchEvent(closedEvent);
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}
